import React, { useMemo } from "react";
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from "@headlessui/react";
import { ArrowsRightLeftIcon, DocumentTextIcon } from "@heroicons/react/24/outline";
import { useOptionalDocumentDrawer } from "../../../views/DocumentDrawer";
import { createTable, TableHeaderSpec } from "../../table/CreateTable.tsx";
import { MoneyLabel } from "../../general/MoneyLabel.tsx";
import type { DrillDownRow } from "./types/types.ts";
import { DrillDownType } from "./types/types.ts";
import { DEFAULT_LOCALE } from "../../../types/constants.ts";
import { drillDownRowsToDrawerDocuments, getFileNameOrDescription } from "./drillDownModal.helper.ts";

export interface DrillDownModalProps {
    open: boolean;
    onClose: () => void;
    title?: string;
    businessId: string;
    emptyMessage?: string;
    rows: DrillDownRow[];
    headingPrefix?: string;
    onDocumentClick?: (documentId: string, fileName: string | null) => void;
}

export function DrillDownModal({
    open,
    onClose,
    title,
    businessId,
    emptyMessage = "Er zijn geen items om te tonen voor deze rubriek.",
    rows,
    headingPrefix = "Documenten",
    onDocumentClick,
}: DrillDownModalProps) {
    const documentDrawer = useOptionalDocumentDrawer();
    if (!onDocumentClick && !documentDrawer) {
        throw new Error('DrillDownModal needs an onDocumentClick handler or a <DocumentDrawerProvider> around it.');
    }
    const headingText = title ? `${headingPrefix} – ${title}` : headingPrefix;
    const drawerDocuments = useMemo(() => drillDownRowsToDrawerDocuments(rows, businessId), [rows, businessId]);

    const showVatBreakdown = rows.some((row) => row.netAmount != null || row.vatAmount != null);

    const amountColumn = (
        field: string,
        name: string,
        pick: (row: DrillDownRow) => number | null | undefined,
    ) => ({
        field,
        name,
        customRender: (row: DrillDownRow) => {
            const value = pick(row);
            return value != null && row.currency ? (
                <div className="font-medium text-gray-900">
                    <MoneyLabel value={value / 100} currency={row.currency} showCurrency={true} />
                </div>
            ) : (
                <span>—</span>
            );
        },
    });

    const spec: TableHeaderSpec<DrillDownRow> = {
        columns: [
            {
                field: "type",
                name: "Type",
                customRender: (row) =>
                    row.type === DrillDownType.TRANSACTION ? (
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-700 ring-1 ring-inset ring-gray-200/60">
                            <ArrowsRightLeftIcon className="size-3.5 shrink-0" aria-hidden />
                            Transactie
                        </span>
                    ) : (
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-700 ring-1 ring-inset ring-gray-200/60">
                            <DocumentTextIcon className="size-3.5 shrink-0" aria-hidden />
                            Factuur
                        </span>
                    ),
            },
            {
                field: "description",
                name: "Details",
                customRender: (row) => <span>{getFileNameOrDescription(row) || "—"}</span>,
            },
            {
                field: "relation_name",
                name: "Relatie",
                customRender: (row) => <span>{row.relation_name ?? "—"}</span>,
            },
            {
                field: "date",
                name: "Datum",
                customRender: (row) =>
                    row.date ? (
                        <span>{new Date(row.date).toLocaleDateString(DEFAULT_LOCALE, { day: "2-digit", month: "2-digit", year: "numeric" })}</span>
                    ) : (
                        <span>—</span>
                    ),
            },
            ...(showVatBreakdown
                ? [
                    amountColumn("netAmount", "Netto", (row) => row.netAmount),
                    amountColumn("vatAmount", "BTW", (row) => row.vatAmount),
                ]
                : []),
            amountColumn("amount", "Bedrag", (row) => row.totalAmount),
        ],
        onRowClick: (row) => {
            if (row.type !== DrillDownType.BILL) {
                return;
            }
            if (onDocumentClick) {
                onDocumentClick(row.documentId, row.file_name);
            } else {
                documentDrawer?.open(businessId, row.documentId, {documentList: drawerDocuments});
            }
        },
        options: { allowFullRowClick: true },
    };

    const Table = createTable(rows, spec);

    return (
        <Dialog open={open} onClose={onClose} className="relative z-40">
            <DialogBackdrop
                transition
                className="fixed inset-0 bg-black/30 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-150 data-[enter]:ease-out data-[leave]:ease-in"
            />
            <div className="fixed inset-0 flex items-center justify-center p-4">
                <DialogPanel
                    transition
                    className="w-full max-w-4xl rounded-lg bg-white p-6 shadow-xl transition-all data-[closed]:opacity-0 data-[closed]:scale-95 data-[enter]:duration-200 data-[leave]:duration-150 data-[enter]:ease-out data-[leave]:ease-in"
                >
                    <div className="flex items-center justify-between mb-4">
                        <DialogTitle as="h3" className="text-xl font-semibold text-gray-900">
                            {headingText}
                        </DialogTitle>
                        <button
                            type="button"
                            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                            onClick={onClose}
                            aria-label="Sluiten"
                        >
                            <svg className="h-6 w-6 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    <div className="max-h-[60vh] overflow-auto">
                        {rows.length === 0 ? (
                            <div className="text-sm text-gray-500">{emptyMessage}</div>
                        ) : (
                            <Table />
                        )}
                    </div>

                    <div className="mt-6 flex justify-end">
                        <button
                            type="button"
                            className="inline-flex justify-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:opacity-70 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                            onClick={onClose}
                        >
                            Sluiten
                        </button>
                    </div>
                </DialogPanel>
            </div>
        </Dialog>
    );
}
